import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase, type TestResult } from '@/lib/supabase';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

import CompositeReport from './CompositeReport';
import { type CompositeInput } from '@/lib/compositeAnalysis';
import { generatePrintHtml, type StudentSummary } from './resultPrint';

const TEST_TYPE_META: Record<string, { label: string; icon: string }> = {
  career: { label: '진로·계열·직업 탐색', icon: 'ri-compass-3-line' },
  iq: { label: 'IQ 테스트', icon: 'ri-brain-line' },
  literacy: { label: '어휘력·문해력 검사', icon: 'ri-book-read-line' },
  study_style: { label: '성격·성향 기반 공부법 검사', icon: 'ri-lightbulb-line' },
  english: { label: '영어 단어 검사', icon: 'ri-book-open-line' },
};

const PRINT_TYPES = [
  { key: 'literacy', label: '어휘력·문해력 검사', icon: 'ri-book-read-line' },
  { key: 'iq', label: 'IQ 테스트', icon: 'ri-brain-line' },
  { key: 'english', label: '영어 단어 검사', icon: 'ri-book-open-line' },
  { key: 'study_style', label: '성격·성향 기반 공부법 검사', icon: 'ri-lightbulb-line' },
  { key: 'career', label: '진로·계열·직업 탐색', icon: 'ri-compass-3-line' },
];

// 종합 융합 분석에 필요한 전체 검사 유형 (5종 모두 완료해야 분석 가능)
const COMPOSITE_TEST_TYPES = ['career', 'study_style', 'iq', 'literacy', 'english'] as const;

function formatDate(iso: string) {
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export default function PortfolioView({ student }: { student: StudentSummary }) {
  const [results, setResults] = useState<TestResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [compositeSelection, setCompositeSelection] = useState<Record<string, string>>({});
  const [showComposite, setShowComposite] = useState(false);

  // Composite 분석용 입력 데이터 구성 (선택된 결과 기준)
  const compositeInput = useMemo(() => {
    const findResult = (type: string) => {
      const id = compositeSelection[type];
      const r = results.find((x) => x.id === id);
      return r?.result;
    };
    return {
      career: findResult('career') as CompositeInput['career'],
      study_style: findResult('study_style') as CompositeInput['study_style'],
      iq: findResult('iq') as CompositeInput['iq'],
      english: findResult('english') as CompositeInput['english'],
      literacy: findResult('literacy') as CompositeInput['literacy'],
    };
  }, [results, compositeSelection]);

  // 모든 검사(5종)를 완료했는지 여부
  const allCompositeDone = COMPOSITE_TEST_TYPES.every((key) =>
    results.some((r) => r.test_type === key),
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('test_results')
        .select('*')
        .eq('student_id', student.id)
        .order('created_at', { ascending: false });
      if (err) throw err;
      const list = (data as TestResult[]) || [];
      setResults(list);
      // 종합 분석용 기본 선택: 각 검사 유형의 최신(첫 번째) 결과를 자동 선택
      const sel: Record<string, string> = {};
      COMPOSITE_TEST_TYPES.forEach((key) => {
        const first = list.find((r) => r.test_type === key);
        if (first) sel[key] = first.id;
      });
      setCompositeSelection(sel);
      setShowComposite(false);
    } catch (e) {
      setError('검사 기록을 불러오지 못했습니다.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [student.id]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(results.map((r) => r.id)));
  };

  const clearSelect = () => {
    setSelectedIds(new Set());
  };

  const handlePrint = () => {
    const selectedResults = results.filter((r) => selectedIds.has(r.id));
    if (selectedResults.length === 0) return;

    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:absolute;top:-9999px;left:-9999px;width:0;height:0;border:none;';
    document.body.appendChild(iframe);

    const cw = iframe.contentWindow;
    const doc = iframe.contentDocument || cw?.document;
    if (!doc || !cw) {
      if (document.body.contains(iframe)) document.body.removeChild(iframe);
      window.print();
      return;
    }

    const html = generatePrintHtml(selectedResults, student, compositeInput);
    doc.open();
    doc.write(html);
    doc.close();

    setTimeout(() => {
      try {
        cw.focus();
        cw.print();
      } catch {
        window.print();
      }
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 5000);
    }, 300);
  };

  const handleDownloadPdf = async () => {
    const selectedResults = results.filter((r) => selectedIds.has(r.id));
    if (selectedResults.length === 0) return;

    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:absolute;top:-9999px;left:-9999px;width:794px;height:1123px;border:none;background:#fff;';
    document.body.appendChild(iframe);

    const cw = iframe.contentWindow;
    const doc = iframe.contentDocument || cw?.document;
    if (!doc || !cw) {
      if (document.body.contains(iframe)) document.body.removeChild(iframe);
      return;
    }

    const html = generatePrintHtml(selectedResults, student, compositeInput);
    doc.open();
    doc.write(html);
    doc.close();

    // 렌더링 대기
    await new Promise((resolve) => setTimeout(resolve, 400));

    try {
      const canvas = await html2canvas(doc.body, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: 794,
      });
      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position -= pageHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`${student.name}_검사결과.pdf`);
    } catch (e) {
      window.alert('PDF 생성 중 오류가 발생했습니다. 인쇄 기능을 이용해 주세요.');
      console.error(e);
    } finally {
      if (document.body.contains(iframe)) document.body.removeChild(iframe);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('이 검사 결과를 삭제하시겠습니까? 삭제 후 복구할 수 없습니다.')) return;
    try {
      const { error: err } = await supabase.from('test_results').delete().eq('id', id);
      if (err) throw err;
      setResults((prev) => prev.filter((r) => r.id !== id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (e) {
      window.alert('삭제 중 오류가 발생했습니다.');
      console.error(e);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`선택한 ${selectedIds.size}건의 검사 결과를 삭제하시겠습니까? 삭제 후 복구할 수 없습니다.`)) {
      return;
    }
    try {
      const ids = Array.from(selectedIds);
      const { error: err } = await supabase.from('test_results').delete().in('id', ids);
      if (err) throw err;
      setResults((prev) => prev.filter((r) => !selectedIds.has(r.id)));
      setSelectedIds(new Set());
    } catch (e) {
      window.alert('삭제 중 오류가 발생했습니다.');
      console.error(e);
    }
  };

  const isAllSelected = results.length > 0 && selectedIds.size === results.length;

  return (
    <div className="bg-white rounded-xl border border-background-200 p-6">
      {/* 학생 헤더 + 인쇄 */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 font-bold text-lg">
              {student.name.charAt(0)}
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950">
                {student.name}
              </h2>
              <div className="text-sm text-foreground-500 flex items-center gap-2 flex-wrap">
                <span>{student.grade || '학년 미정'}</span>
                <span>·</span>
                <span>{student.school || '학교 미기입'}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleDownloadPdf}
            disabled={selectedIds.size === 0}
            className="whitespace-nowrap inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 disabled:bg-background-200 disabled:text-foreground-400 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-colors cursor-pointer"
          >
            <i className="ri-file-download-line"></i>
            PDF 다운로드{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
          </button>
          <button
            onClick={handlePrint}
            disabled={selectedIds.size === 0}
            className="whitespace-nowrap inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:bg-background-200 disabled:text-foreground-400 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-colors cursor-pointer"
          >
            <i className="ri-printer-line"></i>
            인쇄{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : error ? (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          {error}
        </div>
      ) : (
        <div className="space-y-6">
          {/* 선택 컨트롤 */}
          <div className="flex items-center justify-between flex-wrap gap-3 bg-background-50 border border-background-200 rounded-lg px-4 py-3">
            <div className="text-sm text-foreground-500">
              총 <strong className="text-foreground-900">{results.length}</strong>회의 검사 기록
              {selectedIds.size > 0 && (
                <span className="ml-2 text-primary-700 font-medium">
                  · {selectedIds.size}건 선택됨
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={isAllSelected ? clearSelect : selectAll}
                className="whitespace-nowrap text-xs font-medium text-foreground-600 bg-white border border-background-200 rounded-md px-3 py-1.5 hover:bg-background-100 cursor-pointer"
              >
                {isAllSelected ? '전체 해제' : '전체 선택'}
              </button>
              <button
                onClick={handleDeleteSelected}
                disabled={selectedIds.size === 0}
                className="whitespace-nowrap text-xs font-medium text-red-600 bg-white border border-red-200 rounded-md px-3 py-1.5 hover:bg-red-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <i className="ri-delete-bin-line mr-1"></i>
                선택 삭제{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
              </button>
            </div>
          </div>

          {PRINT_TYPES.map(({ key }) => {
            const typeResults = results.filter((r) => r.test_type === key);
            const meta = TEST_TYPE_META[key];
            const hasResult = typeResults.length > 0;

            return (
              <div key={key} className="result-group">
                <div className="result-group-title">
                  <span className={`icon-box ${meta.tone || 'bg-secondary-100 text-secondary-700'}`}>
                    <i className={meta.icon}></i>
                  </span>
                  <span className="label">{meta.label}</span>
                  <span className="count">{hasResult ? `${typeResults.length}회 기록` : '내용없음'}</span>
                </div>

                {hasResult ? (
                  <div className="space-y-2">
                    {typeResults.map((r) => {
                      const checked = selectedIds.has(r.id);
                      return (
                        <div
                          key={r.id}
                          className="result-item cursor-pointer"
                          onClick={() => toggleSelect(r.id)}
                        >
                          <span
                            className={`inline-flex items-center justify-center w-5 h-5 rounded-md border flex-shrink-0 ${
                              checked
                                ? 'bg-primary-500 border-primary-500 text-white'
                                : 'bg-white border-background-300 text-transparent'
                            }`}
                          >
                            <i className="ri-check-line text-sm"></i>
                          </span>
                          <span className="date">{formatDate(r.created_at)}</span>
                          <span className="title">{r.title || meta.label}</span>
                          {r.score != null && <span className="score">{r.score}점</span>}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(r.id);
                            }}
                            className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0"
                            title="삭제"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="result-empty">
                    <i className="ri-file-forbid-line"></i>
                    <span>해당 검사를 아직 진행하지 않았습니다.</span>
                  </div>
                )}
              </div>
            );
          })}

          {/* 기타 검사 결과 */}
          {(() => {
            const extraTypes = results
              .map((r) => r.test_type)
              .filter((t) => !PRINT_TYPES.some((p) => p.key === t));
            const uniqueExtras = Array.from(new Set(extraTypes));
            if (uniqueExtras.length === 0) return null;

            return uniqueExtras.map((type) => {
              const typeResults = results.filter((r) => r.test_type === type);
              const meta = TEST_TYPE_META[type] || { label: type, icon: 'ri-file-list-3-line' };
              return (
                <div key={type} className="result-group">
                  <div className="result-group-title">
                    <span className="icon-box bg-secondary-100 text-secondary-700">
                      <i className={meta.icon}></i>
                    </span>
                    <span className="label">{meta.label}</span>
                    <span className="count">{typeResults.length}회 기록</span>
                  </div>
                  <div className="space-y-2">
                    {typeResults.map((r) => {
                      const checked = selectedIds.has(r.id);
                      return (
                        <div
                          key={r.id}
                          className="result-item cursor-pointer"
                          onClick={() => toggleSelect(r.id)}
                        >
                          <span
                            className={`inline-flex items-center justify-center w-5 h-5 rounded-md border flex-shrink-0 ${
                              checked
                                ? 'bg-primary-500 border-primary-500 text-white'
                                : 'bg-white border-background-300 text-transparent'
                            }`}
                          >
                            <i className="ri-check-line text-sm"></i>
                          </span>
                          <span className="date">{formatDate(r.created_at)}</span>
                          <span className="title">{r.title || meta.label}</span>
                          {r.score != null && <span className="score">{r.score}점</span>}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(r.id);
                            }}
                            className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0"
                            title="삭제"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}

          {/* 종합 융합 분석 */}
          <div className="border-t border-background-200 pt-6">
            <div className="flex items-center gap-2 mb-1">
              <i className="ri-sparkling-2-line text-accent-500 text-lg" />
              <h3 className="font-bold text-foreground-950 text-base">종합 융합 분석</h3>
            </div>
            <p className="text-sm text-foreground-500 mb-4">
              모든 검사를 완료하면 종합 분석을 실행할 수 있습니다. 여러 번 시행한 검사는 분석에 사용할 결과를 선택해 주세요.
            </p>

            {/* 검사별 완료 상태 + 결과 선택 */}
            <div className="space-y-3 mb-5">
              {COMPOSITE_TEST_TYPES.map((key) => {
                const meta = TEST_TYPE_META[key];
                const typeResults = results.filter((r) => r.test_type === key);
                const done = typeResults.length > 0;
                const selectedId = compositeSelection[key] || typeResults[0]?.id;

                return (
                  <div
                    key={key}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      done ? 'bg-background-50 border-background-200' : 'bg-red-50/60 border-red-100'
                    }`}
                  >
                    <span
                      className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${
                        done ? 'bg-primary-100 text-primary-700' : 'bg-red-100 text-red-500'
                      }`}
                    >
                      <i className={meta.icon}></i>
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-foreground-900">{meta.label}</div>
                      <div className="text-xs text-foreground-500">
                        {done ? `${typeResults.length}회 시행` : '미완료'}
                      </div>
                    </div>
                    {done ? (
                      typeResults.length > 1 ? (
                        <select
                          value={selectedId}
                          onChange={(e) =>
                            setCompositeSelection((prev) => ({ ...prev, [key]: e.target.value }))
                          }
                          className="text-sm px-2 py-1.5 rounded-md border border-background-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer max-w-[210px]"
                        >
                          {typeResults.map((r) => (
                            <option key={r.id} value={r.id}>
                              {formatDate(r.created_at)}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-xs text-primary-700 font-medium whitespace-nowrap">
                          <i className="ri-check-line"></i> 완료
                        </span>
                      )
                    ) : (
                      <span className="text-xs text-red-500 font-medium whitespace-nowrap">검사 필요</span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* 실행 버튼 */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                onClick={() => setShowComposite(true)}
                disabled={!allCompositeDone}
                className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-600 disabled:bg-background-200 disabled:text-foreground-400 disabled:cursor-not-allowed text-white text-sm font-semibold px-6 py-3 rounded-md transition-colors cursor-pointer"
              >
                <i className="ri-sparkling-line"></i>
                종합 융합 분석 실행
              </button>
              {!allCompositeDone && (
                <p className="text-xs text-foreground-500">
                  아직 완료되지 않은 검사가 있습니다. 모든 검사를 완료해야 분석을 실행할 수 있습니다.
                </p>
              )}
            </div>

            {/* 분석 결과 */}
            {showComposite && allCompositeDone && (
              <div className="mt-6">
                <CompositeReport input={compositeInput} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}